import { useCallback, useState } from 'react';
import { useApi, useMutation } from './useApi';
import {
  getClients,
  getClient,
  createClient,
  updateClient,
  ClientFilters,
} from '../services/api';
import {
  Client,
  PaginatedResponse,
  CreateClientRequest,
  UpdateClientRequest,
} from '../types';

export function useClients(initialFilters: ClientFilters = {}) {
  const [filters, setFilters] = useState<ClientFilters>(initialFilters);

  const fetchClients = useCallback(() => getClients(filters), [filters]);

  const { data, loading, error, refetch } = useApi<PaginatedResponse<Client>>(
    fetchClients,
    [JSON.stringify(filters)]
  );

  return {
    clients: data?.items || [],
    total: data?.total || 0,
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
    loading,
    error,
    filters,
    setFilters,
    refetch,
  };
}

// Reserved route segments that should not be treated as client IDs
const RESERVED_ROUTES = ['onboard', 'new', 'create', 'edit'];

export function useClient(id: string | undefined) {
  const fetchClient = useCallback(() => {
    if (!id || RESERVED_ROUTES.includes(id.toLowerCase())) {
      return Promise.reject(new Error('No client ID provided'));
    }
    return getClient(id);
  }, [id]);

  const { data, loading, error, refetch } = useApi<Client>(fetchClient, [id]);

  return {
    client: data,
    loading,
    error,
    refetch,
  };
}

export function useCreateClient() {
  const { mutate, loading, error, data, reset } = useMutation<Client, CreateClientRequest>(
    createClient
  );

  return {
    createClient: mutate,
    loading,
    error,
    createdClient: data,
    reset,
  };
}

export function useUpdateClient() {
  const { mutate, loading, error, data, reset } = useMutation<
    Client,
    { id: string; data: UpdateClientRequest }
  >(({ id, data }) => updateClient(id, data));

  return {
    updateClient: mutate,
    loading,
    error,
    updatedClient: data,
    reset,
  };
}
